import { ActionContext } from 'vuex';
import { TechnicianMutations } from './mutations';
import { generateNotificationItem, NOTIFICATION_TYPES } from '@/models/AppConfig';
import { AppState } from '@/models/TechnicianAppState';
import { TechExisenceValidationPayload, Technician } from '../../../models/Technician';
import { ConfigMutations } from '../config/mutations';
import technicianUtils from '../../../utils/technician';
import router from '../../../router';
import axios, { AxiosError, AxiosResponse } from 'axios';
import config from '../../../config';
import { TechnicianGetters } from './getters';
import ModalManager from '../../../utils/modal';
import TechExistModalContent from '@/components/signUp/TechExistModalContent.vue'
import { errorCodes } from '@puls/puls-common-constants';
import { STATE_MODULES } from '@/store/state-modules';
import { GoogleAddressData, marketUtils } from '@puls/puls-general-utils';
import { marketByZipcodeResponseData } from '../../../models/Market';
import { LoginActions } from '../login/actions';
import { NotificationPrefrence } from '@/models/Common';
import { LoginMutations } from '../login/mutations';

export const TechnicianActions = {
	GET_TECHNICIAN_DETAILS: 'GET_TECHNICIAN_DETAILS',
	CREATE_TECHNICIAN: "CREATE_TECHNICIAN",
	GET_SERVICE_AREAS: "GET_SERVICE_AREAS",
	VALIDATE_USER_DETAILS: "VALIDATE_USER_DETAILS",
	VALIDATE_SELECTED_ADRESS: "VALIDATE_SELECTED_ADRESS",
	UPDATE_PREFERENCES: "UPDATE_PREFERENCES",
	CLAIM_JOB: "CLAIM_JOB"
}

export default {
	async [TechnicianActions.VALIDATE_USER_DETAILS] (context: ActionContext<Technician, AppState>) {
		const techExistencePayload: TechExisenceValidationPayload = {
			email: context.getters[TechnicianGetters.GET_EMAIL],
			phone: context.getters[TechnicianGetters.GET_PHONE]
		}
		try {
			const { data } = await axios.post(`${config.api}/portal/technician/is-exists`, techExistencePayload);
			if (!data.ok) throw new Error('Error validating details')
			router.instance.push('/sign-up/skills');
		} catch (err: any) {
			if (err.response?.status === 400) {
				const respData = err.response.data;
				if (respData.is_valid_for_update && respData.technician_id) {
					ModalManager.open(TechExistModalContent, {}, (isAccepted: boolean) => {
						if (isAccepted) {
							router.instance.push('/sign-up/skills');
						}
					});
				} else {
					const { error_code, message } = respData;
					context.commit(
						`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
						generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: message || "Something went wrong!", code: technicianUtils.getErrorFromCode(error_code) }),
						{ root: true }
					)
				}
			} else {
				context.commit(
					`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
					generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: err.response?.data?.message || "Something went wrong!" }),
					{ root: true }
				)
			}
		}
	},
	async [TechnicianActions.VALIDATE_SELECTED_ADRESS] (context: ActionContext<Technician, AppState>, payload: { place_id: string }) {
		const invalidAddressError = 'Address is not valid, please select from the list';
		try {
			const googleAddressData: GoogleAddressData = await marketUtils.getAddressDataFromPlaceId(payload.place_id);
			const zipcodeParams = {
				zipCode: googleAddressData.zipCode,
				deviceId: 321,
				appointment_funnel_code: 'handyman',
				country: 'US'
			};

			if (!googleAddressData.isGoogleZipCodeValid || !googleAddressData.cityData || !googleAddressData.stateData) throw new Error(invalidAddressError);
			const marketData: AxiosResponse<marketByZipcodeResponseData> = await axios.post(`${config.api}/portal/market/byzipcode`, zipcodeParams);
			if (!marketData.data.isValidZipCode) throw new Error(invalidAddressError);
			context.commit(
				TechnicianMutations.UPDATE_FIELD,
				{ key: 'market_id', value: marketData.data.marketId }
			)
			context.commit(
				TechnicianMutations.UPDATE_FIELD,
				{ key: 'city', value: googleAddressData.cityData }
			)
			context.commit(
				TechnicianMutations.UPDATE_FIELD,
				{ key: 'state', value: googleAddressData.stateData }
			)

			context.commit(
				TechnicianMutations.UPDATE_FIELD,
				{ key: 'address_coordinates', value: { lat: googleAddressData.geometry.location.lat(), lng: googleAddressData.geometry.location.lng() } }
			)
		} catch (err: any) {
			context.commit(
				`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
				generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: err.message, code: errorCodes.ADDRESS_NOT_VALID }),
				{ root: true }
			)
		}
	},
	async [TechnicianActions.CREATE_TECHNICIAN] (context: ActionContext<Technician, AppState>) {
		const techData: Technician = context.getters[TechnicianGetters.GET_DETAILS];
		const { first_name, last_name, phone, email, company_name, address, market_id, radius } = techData;
		const techCreationPayload = {
			first_name,
			last_name,
			email,
			phone: `+1${phone}`,
			full_address: address,
			preferred_location: {
				full_address: address,
				latitude: techData.address_coordinates?.lat,
				longitude: techData.address_coordinates?.lng
			},
			radius,
			rank: 2,
			gender: {
				id: 1
			},
			market: {
				id: market_id
			},
			work_model: {
				id: 2
			},
			service_provider: {
				id: 9
			},
			statusReasons: [],
			skills: Array.from(context.getters[TechnicianGetters.GET_SELECTED_SKILLS]),
			notes: [],
			is_company: true,
			company_name,
			areas: Array.from(context.getters[TechnicianGetters.GET_SELECTED_AREAS])
		}
		try {
			const { data } = await axios.post(`${config.api}/portal/technician/create`, techCreationPayload);
			if (!data.is_succeeded) throw new Error('Failed to create technician');
			const techEmail = context.getters[TechnicianGetters.GET_EMAIL];
			context.commit(`${STATE_MODULES.LOGIN}/${LoginMutations.UPDATE_COMMUNICATION_VALUE}`, { value: techEmail }, { root: true })
			context.commit(
				`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
				generateNotificationItem({ type: NOTIFICATION_TYPES.success, message: "Technician created successfully" }),
				{ root: true }
			)
			context.dispatch(
				`${STATE_MODULES.LOGIN}/${LoginActions.LOGIN}`,
				{},
				{ root: true }
			)
		} catch (err: any) {
			context.commit(
				`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
				generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: err.message || "Something went wrong!" }),
				{ root: true }
			)
		}
	},
	[TechnicianActions.UPDATE_PREFERENCES] (context: ActionContext<Technician, AppState>, payload: { notification_preference: NotificationPrefrence[], areas: Set<number>, radius: number }) {
		axios({
			method: 'PUT',
			url: `${config.api}/portal/technician/lead/update`,
			data: payload
		}).then((response: AxiosResponse) => {
			if (!response?.data?.is_succeeded) {
				throw new Error("Failed to update preferences")
			}

			context.commit(
				`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
				generateNotificationItem({ type: NOTIFICATION_TYPES.success, message: 'Preferences updated successfully' }),
				{ root: true }
			)
			context.dispatch(`${STATE_MODULES.LOGIN}/${LoginActions.INIT_USER}`, {}, { root: true });
		}).catch((err: AxiosError<any>) => {
			context.commit(
				`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
				generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: err.message || "Failed to update preferences" }),
				{ root: true }
			)
		})
	}
}
